import { type ReactNode } from 'react';
import {
  Image as ImageIcon,
  HelpCircle,
  Flag,
  Sparkles,
  List as ListIcon,
  Table as TableIcon,
  BarChart3,
  CheckSquare,
  Zap,
  Quote,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface InsertBlockDef {
  id: string;
  label: string;
  icon: ReactNode;
  /** Visually emphasized chip — e.g. the "Hero Image" coral chip in the design. */
  emphasis?: boolean;
  /** Premium-only block — renders a lock icon and marks it as a paywalled feature. */
  premium?: boolean;
}

export const DEFAULT_INSERT_BLOCKS: InsertBlockDef[] = [
  { id: 'hero', label: 'Hero Image', icon: <ImageIcon className="size-3" />, emphasis: true, premium: true },
  { id: 'faq', label: 'FAQ', icon: <HelpCircle className="size-3" />, premium: true },
  { id: 'conclusion', label: 'Conclusion', icon: <Flag className="size-3" />, premium: true },
  { id: 'humanize', label: 'Humanize', icon: <Sparkles className="size-3" />, premium: true },
  { id: 'list', label: 'List', icon: <ListIcon className="size-3" />, premium: true },
  { id: 'table', label: 'Table', icon: <TableIcon className="size-3" />, premium: true },
  { id: 'infographic', label: 'Infographic', icon: <BarChart3 className="size-3" />, premium: true },
  { id: 'takeaways', label: 'Takeaways', icon: <CheckSquare className="size-3" />, premium: true },
  { id: 'cta', label: 'CTA', icon: <Zap className="size-3" />, premium: true },
  { id: 'quote', label: 'Quote', icon: <Quote className="size-3" />, premium: true },
];

export interface InsertBlockBarProps {
  blocks?: InsertBlockDef[];
  onInsert?: (id: string) => void;
}

/**
 * Row 5 of DocChrome — chip row of insertable blocks.
 * Sits on the warm-bg background, scrolls horizontally if cramped.
 * Premium blocks render a lock icon to indicate they are paywalled.
 */
export function InsertBlockBar({
  blocks = DEFAULT_INSERT_BLOCKS,
  onInsert,
}: InsertBlockBarProps) {
  return (
    <div className="flex items-center gap-1.5 px-3.5 py-1.5 border-b border-border bg-warm-bg overflow-x-auto flex-nowrap">
      <span className="text-[10.5px] font-bold text-muted-foreground uppercase tracking-wider mr-1.5 shrink-0">
        Insert
      </span>
      {/* removed Write-with-AI (broken) */}
      {blocks.map((b) => (
        <button
          key={b.id}
          type="button"
          // Premium chips are locked: clicking is a no-op (rely on tooltip).
          onClick={() => {
            if (b.premium) return;
            onInsert?.(b.id);
          }}
          aria-disabled={b.premium || undefined}
          title={b.premium ? 'Premium only' : undefined}
          className={cn(
            'inline-flex items-center gap-1.5 shrink-0 h-6 px-2.5 rounded-full border text-[11.5px] font-medium transition-colors',
            b.premium
              ? 'cursor-not-allowed bg-muted/40 border-border text-muted-foreground opacity-60'
              : b.emphasis
                ? 'cursor-pointer bg-coral-light border-coral/25 text-coral-dark hover:bg-coral-light/80'
                : 'cursor-pointer bg-card border-border text-muted-foreground hover:bg-warm-bg hover:text-foreground',
          )}
        >
          <span className="inline-flex shrink-0">{b.icon}</span>
          {b.label}
          {b.premium && (
            <Lock
              className="size-2.5 shrink-0 opacity-70"
              aria-label="Premium only"
            />
          )}
        </button>
      ))}
    </div>
  );
}
